import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { fmtDate, fmtMoneyCur, UPE_COLORS } from "../lib/format";
import { Card, Ring, SectionTitle, ProgressBar } from "../components/shared";
import HitosCard from "../components/HitosCard";
import GridMap from "../components/maps/GridMap";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell } from "recharts";
import { FileDown, AlertTriangle, Flag, Activity, CalendarClock, TrendingUp, ShieldAlert, Files } from "lucide-react";


export default function Dashboard() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = () => api.get(`/properties/${pid}/dashboard`).then((r) => setData(r.data));
  useEffect(() => {
    setData(null);
    load();
  }, [pid]); // eslint-disable-line

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/properties/${pid}/reporte-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_ejecutivo_${pid}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte ejecutivo descargado");
    } catch (e) { toast.error(apiError(e)); }
    setExporting(false);
  };

  if (!data) return <div className="p-10 text-slate-400 animate-pulse">Cargando vista general...</div>;
  const k = data.kpis;
  const nivelColor = { municipal: "#10B981", estatal: "#F59E0B", federal: "#EF4444" };

  return (
    <div className="p-4 sm:p-8 space-y-6 max-w-[1400px]">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Vista General del Macroproyecto</p>
          <h1 className="font-display text-3xl font-bold text-[#002B49]">Resumen integral del proyecto</h1>
        </div>
        <Button data-testid="export-pdf-button" onClick={exportPdf} disabled={exporting}
          className="bg-[#002B49] hover:bg-[#001D33] text-white gap-2 active:scale-[0.98]">
          <FileDown size={15} /> {exporting ? "Generando..." : "Exportar reporte ejecutivo"}
        </Button>
      </motion.div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { icon: TrendingUp, label: "Avance global", value: `${k.avance_global}%`, testId: "kpi-avance-global" },
          { icon: Files, label: "Trámites totales", value: k.tramites_total, testId: "kpi-tramites-total" },
          { icon: Flag, label: "Aprobados", value: k.aprobados, testId: "kpi-aprobados" },
          { icon: ShieldAlert, label: "En prevención", value: k.en_prevencion, testId: "kpi-prevencion", warn: k.en_prevencion > 0 },
          { icon: CalendarClock, label: "Por vencer (30 días)", value: k.por_vencer_30, testId: "kpi-por-vencer", warn: k.por_vencer_30 > 0 },
          { icon: AlertTriangle, label: "Alertas activas", value: k.alertas_activas, testId: "kpi-alertas", warn: k.alertas_activas > 0 },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}>
            <Card className="p-4 h-full" testId={kpi.testId}>
              <kpi.icon size={16} className={kpi.warn ? "text-amber-500" : "text-[#00A3B5]"} />
              <p className="font-display text-2xl font-bold text-[#002B49] mt-2 leading-none">{kpi.value}</p>
              <p className="text-[11px] text-slate-500 mt-1.5">{kpi.label}</p>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* KPIs Gestión / Desarrollo / Financiamiento / Construcción */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="p-5" testId="kpi-gestion">
          <SectionTitle right={<Link to={`/p/${pid}/gestion`} className="text-xs font-bold text-[#00A3B5] hover:underline uppercase tracking-wider">Abrir →</Link>}>Gestión</SectionTitle>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Tareas abiertas", data.gestion?.tareas_abiertas ?? 0], ["% acuerdos en tiempo", `${data.gestion?.pct_acuerdos_en_tiempo ?? 0}%`],
              ["Acuerdos vencidos", data.gestion?.acuerdos_vencidos ?? 0]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-md p-2.5">
                <p className="font-display font-bold text-lg text-[#002B49]">{v}</p>
                <p className="text-[9px] uppercase tracking-wider text-slate-500">{l}</p>
              </div>))}
          </div>
        </Card>
        <Card className="p-5" testId="kpi-desarrollo">
          <SectionTitle right={<Link to={`/p/${pid}/desarrollo`} className="text-xs font-bold text-[#00A3B5] hover:underline uppercase tracking-wider">Abrir →</Link>}>Desarrollo</SectionTitle>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Unidades def./aut.", `${data.desarrollo?.unidades_definidas ?? 0}/${data.desarrollo?.unidades_autorizadas ?? 0}`],
              ["Inversión estimada", fmtMoneyCur(data.desarrollo?.inversion_estimada ?? 0)],
              ["% estándares", `${data.desarrollo?.pct_estandares ?? 0}%`]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-md p-2.5">
                <p className="font-display font-bold text-sm text-[#002B49] leading-tight">{v}</p>
                <p className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5">{l}</p>
              </div>))}
          </div>
        </Card>
        <Card className="p-5" testId="kpi-finanzas">
          <SectionTitle right={<Link to={`/p/${pid}/creditos`} className="text-xs font-bold text-[#00A3B5] hover:underline uppercase tracking-wider">Abrir →</Link>}>Financiamiento</SectionTitle>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Deuda disp./aut.", `${fmtMoneyCur(data.finanzas?.deuda_dispuesta ?? 0)} / ${fmtMoneyCur(data.finanzas?.deuda_autorizada ?? 0)}`],
              ["Próximo pago", data.finanzas?.proximo_pago ? fmtDate(data.finanzas.proximo_pago.fecha) : "—"],
              ["Covenants en riesgo", data.finanzas?.covenants_en_riesgo ?? 0]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-md p-2.5">
                <p className="font-display font-bold text-sm text-[#002B49] leading-tight">{v}</p>
                <p className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5">{l}</p>
              </div>))}
          </div>
        </Card>
        <Card className="p-5" testId="kpi-construccion">
          <SectionTitle right={<Link to={`/p/${pid}/construccion`} className="text-xs font-bold text-[#00A3B5] hover:underline uppercase tracking-wider">Abrir →</Link>}>Construcción</SectionTitle>
          <div className="grid grid-cols-2 gap-2 text-center">
            {[["Avance físico", `${data.construccion?.avance_fisico ?? 0}%`],
              ["Certificados", data.construccion?.certificados ?? 0]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-md p-2.5">
                <p className="font-display font-bold text-lg text-[#002B49]">{v}</p>
                <p className="text-[9px] uppercase tracking-wider text-slate-500">{l}</p>
              </div>))}
          </div>
          <ProgressBar value={data.construccion?.avance_fisico ?? 0} className="mt-3" />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Donuts por nivel */}
        <Card className="p-6 lg:col-span-4" testId="donuts-niveles">
          <SectionTitle>Permisología por nivel de gobierno</SectionTitle>
          <div className="flex items-center justify-around">
            {["municipal", "estatal", "federal"].map((n) => (
              <div key={n} className="text-center">
                <Ring value={k.por_nivel[n].avance} color={nivelColor[n]} size={92} />
                <p className="text-xs font-bold text-slate-600 mt-2 capitalize">{n}</p>
                <p className="text-[10px] text-slate-400">{k.por_nivel[n].aprobados}/{k.por_nivel[n].total} aprobados</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Mini-mapa */}
        <Card className="p-6 lg:col-span-8" testId="mini-mapa">
          <SectionTitle right={
            <Link to={`/p/${pid}/mapa`} data-testid="ver-mapa-link" className="text-xs font-bold text-[#00A3B5] hover:underline uppercase tracking-wider">Ver Mapa Maestro →</Link>
          }>Mini-mapa de UPEs</SectionTitle>
          <GridMap upes={data.upes} mini onSelect={() => navigate(`/p/${pid}/mapa`)} />
        </Card>

        {/* Hitos */}
        <HitosCard hitos={data.hitos} pid={pid} onChanged={load} />

        {/* Cuellos de botella */}
        <Card className="p-6 lg:col-span-6" testId="cuellos-botella">
          <SectionTitle>Cuellos de botella por autoridad</SectionTitle>
          {data.cuellos.length === 0 ? <p className="text-sm text-slate-400">Sin trámites detenidos.</p> : (
            <ResponsiveContainer width="100%" height={Math.max(160, data.cuellos.length * 42)}>
              <BarChart data={data.cuellos} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="autoridad" width={110} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                <RTooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  formatter={(v) => [`${v} trámites detenidos`, ""]} />
                <Bar dataKey="tramites" radius={[0, 4, 4, 0]} barSize={18}>
                  {data.cuellos.map((c, i) => <Cell key={i} fill={i === 0 ? "#DC2626" : i === 1 ? "#D97706" : "#00A3B5"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Avance por etapa */}
        <Card className="p-6 lg:col-span-6" testId="avance-etapas">
          <SectionTitle>Avance por checklist / etapa</SectionTitle>
          <div className="space-y-4">
            {data.etapas.map((e) => (
              <div key={e.etapa}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-600 font-semibold truncate pr-4">{e.etapa}</span>
                  <span className="text-[#002B49] font-bold whitespace-nowrap">{e.avance}% · {e.aprobados}/{e.total}</span>
                </div>
                <ProgressBar value={e.avance} />
              </div>
            ))}
          </div>
        </Card>

        {/* Vencimientos */}
        <Card className="p-6 lg:col-span-3" testId="proximos-vencimientos">
          <SectionTitle>Próximos vencimientos</SectionTitle>
          <div className="space-y-3">
            {data.vencimientos.length === 0 && <p className="text-sm text-slate-400">Sin vigencias registradas.</p>}
            {data.vencimientos.map((v) => (
              <Link key={v.tramite_id} to={`/p/${pid}/tramites/${v.tramite_id}`} className="block group">
                <p className="text-sm font-semibold text-slate-800 group-hover:text-[#00A3B5] transition-colors line-clamp-1">{v.nombre}</p>
                <p className={`text-xs font-bold ${v.dias <= 15 ? "text-red-600" : v.dias <= 60 ? "text-amber-600" : "text-emerald-600"}`}>
                  {fmtDate(v.fecha)} · {v.dias < 0 ? `vencido hace ${-v.dias} días` : `${v.dias} días`}
                </p>
              </Link>
            ))}
          </div>
        </Card>

        {/* Actividad reciente */}
        <Card className="p-6 lg:col-span-3" testId="actividad-reciente">
          <SectionTitle>Actividad reciente</SectionTitle>
          <div className="space-y-3">
            {data.actividad.map((a) => (
              <div key={a.id} className="flex gap-2 text-xs">
                <Activity size={13} className="text-slate-300 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-slate-700"><span className="font-semibold">{a.usuario}</span> — {a.accion}</p>
                  <p className="text-slate-400 line-clamp-1">{a.entidad_nombre}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        {Object.entries(UPE_COLORS).map(([e, c]) => (
          <span key={e} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-slate-300" style={{ background: c }} />{e}</span>
        ))}
      </div>
    </div>
  );
}
